/**
 * RecordsBrowserTab — Wave 6.3 main filter / bulk-select surface.
 *
 * Lists records across Cart / CartProduct / MaintAction with filters per the
 * Wave 6.3 brief: origin, resolution, age, has-artifact, is-test-mode,
 * officer, branch.
 *
 * Row-level kebab: "Hard-delete with reason" (opens HardDeleteDialog),
 * "Correct metadata" (opens BulkCorrectionsDialog with the row pre-selected),
 * "Retention hold" (opens an inline date picker that posts to retention-hold).
 *
 * Bulk-select drives BulkCorrectionsDialog (multiple rows) or a bulk
 * hard-delete via the same HardDeleteDialog.
 */

import { useEffect, useMemo, useState } from 'react';
import type {
  CartResolutionCode,
  Origin,
  PurgeEntityType,
  RecordsBrowseFilters,
  RecordsBrowseRow,
} from '@retail-ops/shared-types';
import { Banner, Button, Pill } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';
import { useRecordsApi } from '../hooks/useRecordsApi';
import { HardDeleteDialog } from '../dialogs/HardDeleteDialog';
import { BulkCorrectionsDialog } from '../dialogs/BulkCorrectionsDialog';

const ENTITY_TYPES: ReadonlyArray<{ value: PurgeEntityType | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'Cart', label: 'Carts' },
  { value: 'CartProduct', label: 'Cart products' },
  { value: 'MaintAction', label: 'Maintenance' },
];

const ORIGINS: ReadonlyArray<{ value: Origin | 'all'; label: string }> = [
  { value: 'all', label: 'All origins' },
  { value: 'in-branch', label: 'Branch FSR' },
  { value: 'fsr-inbranch', label: 'FSR — in-branch' },
  { value: 'banno', label: 'Banno' },
  { value: 'online', label: 'Online' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'phone', label: 'Phone' },
  { value: 'kiosk', label: 'Kiosk' },
];

const RESOLUTIONS: ReadonlyArray<{ value: CartResolutionCode | 'all'; label: string }> = [
  { value: 'all', label: 'All resolutions' },
  { value: 'Open', label: 'Open' },
  { value: 'Processed', label: 'Processed' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'Adverse-Action', label: 'Adverse Action' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'System-Error', label: 'System Error' },
];

const AGE_BUCKETS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: 'Any age' },
  { value: 1, label: '1+ days' },
  { value: 7, label: '7+ days' },
  { value: 30, label: '30+ days' },
  { value: 365, label: '1+ years' },
];

interface DialogState {
  kind: 'idle' | 'hard-delete' | 'bulk-correct';
  rows: ReadonlyArray<RecordsBrowseRow>;
}

export function RecordsBrowserTab(): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const { config } = useConfig();
  const [filters, setFilters] = useState<RecordsBrowseFilters>({});
  const [entityFilter, setEntityFilter] = useState<PurgeEntityType | 'all'>('all');
  const [originFilter, setOriginFilter] = useState<Origin | 'all'>('all');
  const [resolutionFilter, setResolutionFilter] = useState<CartResolutionCode | 'all'>('all');
  const [ageFilter, setAgeFilter] = useState<number>(0);
  const [hasArtifactFilter, setHasArtifactFilter] = useState<'any' | 'true' | 'false'>('any');
  const [testModeFilter, setTestModeFilter] = useState<'any' | 'true' | 'false'>('any');
  const [officerFilter, setOfficerFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [nameQuery, setNameQuery] = useState<string>('');
  const [rows, setRows] = useState<ReadonlyArray<RecordsBrowseRow>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<DialogState>({ kind: 'idle', rows: [] });

  const branchOptions = useMemo(
    () => [
      { value: 'all', label: 'All branches' },
      ...config.branches.map((b) => ({ value: b.branchCode, label: b.branchName })),
    ],
    [config.branches],
  );

  function buildFilters(): RecordsBrowseFilters {
    const f: RecordsBrowseFilters = { ...filters };
    if (entityFilter !== 'all') f.entityType = entityFilter;
    if (originFilter !== 'all') f.origin = originFilter as Origin;
    if (resolutionFilter !== 'all') f.resolution = resolutionFilter as CartResolutionCode;
    if (ageFilter > 0) f.ageDays = ageFilter;
    if (hasArtifactFilter !== 'any') f.hasArtifact = hasArtifactFilter === 'true';
    if (testModeFilter !== 'any') f.isTestMode = testModeFilter === 'true';
    if (officerFilter !== 'all') f.officerCode = officerFilter;
    if (branchFilter !== 'all') f.branch = branchFilter;
    if (nameQuery.trim().length > 0) f.customerNameQuery = nameQuery.trim();
    return f;
  }

  async function loadRows(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await client.retail.superAdmin.records.browse(buildFilters(), {
        signal: makeSignal(),
      });
      const anyRes = res as unknown as {
        success?: boolean;
        data?: RecordsBrowseRow[];
        error?: string;
      };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to load records.');
        setRows([]);
      } else {
        setRows(anyRes.data ?? []);
        setSelected(new Set());
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to load records.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleRow(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAll(): void {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => `${r.entityType}::${r.entityKey}`)));
  }

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(`${r.entityType}::${r.entityKey}`)),
    [rows, selected],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <Banner tone="info">
        Hard-delete here is <strong>permanent in SQL</strong> but a JSON snapshot is always
        written to <code>ao_PurgeRecords</code> first — restore via the Recovery tab if you
        change your mind. Use Bulk corrections for safe edits to AssignedOfficerCode / branch /
        resolution / retention; OriginRole is locked for audit integrity.
      </Banner>

      <FiltersPanel
        entityFilter={entityFilter}
        setEntityFilter={setEntityFilter}
        originFilter={originFilter}
        setOriginFilter={setOriginFilter}
        resolutionFilter={resolutionFilter}
        setResolutionFilter={setResolutionFilter}
        ageFilter={ageFilter}
        setAgeFilter={setAgeFilter}
        hasArtifactFilter={hasArtifactFilter}
        setHasArtifactFilter={setHasArtifactFilter}
        testModeFilter={testModeFilter}
        setTestModeFilter={setTestModeFilter}
        branchFilter={branchFilter}
        setBranchFilter={setBranchFilter}
        branchOptions={branchOptions}
        officerFilter={officerFilter}
        setOfficerFilter={setOfficerFilter}
        nameQuery={nameQuery}
        setNameQuery={setNameQuery}
        onApply={() => void loadRows()}
      />

      {error && (
        <Banner tone="err" title="Failed to load">
          {error}
        </Banner>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-4)',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          {loading ? 'Loading…' : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
          {selected.size > 0 && (
            <span style={{ marginLeft: 'var(--sp-3)', color: 'var(--pcb-blue)', fontWeight: 600 }}>
              {selected.size} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <Button
            variant="ghost"
            size="sm"
            disabled={selected.size === 0}
            onClick={() => setDialog({ kind: 'bulk-correct', rows: selectedRows })}
          >
            Bulk correct…
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={selected.size === 0}
            onClick={() => setDialog({ kind: 'hard-delete', rows: selectedRows })}
          >
            Hard-delete selected…
          </Button>
        </div>
      </div>

      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          overflow: 'auto',
          maxHeight: '60vh',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-100)', textAlign: 'left' }}>
              <th style={th({ width: 28 })}>
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={selected.size > 0 && selected.size === rows.length}
                  onChange={toggleAll}
                />
              </th>
              <th style={th()}>Type</th>
              <th style={th()}>Record</th>
              <th style={th()}>Customer</th>
              <th style={th()}>Branch / Officer</th>
              <th style={th()}>Resolution</th>
              <th style={th()}>Age</th>
              <th style={th()}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={8} style={{ padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--ink-600)' }}>
                  No records match the current filters.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const key = `${row.entityType}::${row.entityKey}`;
              const isSel = selected.has(key);
              return (
                <tr key={key} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                  <td style={td()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.displayLabel}`}
                      checked={isSel}
                      onChange={() => toggleRow(key)}
                    />
                  </td>
                  <td style={td()}>
                    <Pill variant="muted">{row.entityType}</Pill>
                  </td>
                  <td style={td()}>
                    <div style={{ fontWeight: 600 }}>{row.displayLabel}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>
                      {row.hasRetentionHold && (
                        <span style={{ marginRight: 'var(--sp-2)' }}>
                          <Pill variant="warn">Retention hold</Pill>
                        </span>
                      )}
                      {row.isTestMode && <TestModeBadge active size="sm" />}
                    </div>
                  </td>
                  <td style={td()}>{row.customerName ?? '—'}</td>
                  <td style={td()}>
                    <div>{row.branchName ?? row.branch ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>
                      {row.officerDisplayName ?? row.officerCode ?? '—'}
                    </div>
                  </td>
                  <td style={td()}>{row.resolutionCode ?? <span style={{ color: 'var(--ink-400)' }}>—</span>}</td>
                  <td style={td({ whiteSpace: 'nowrap' })}>{row.ageDays}d</td>
                  <td style={td()}>
                    <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDialog({ kind: 'bulk-correct', rows: [row] })}
                      >
                        Correct
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDialog({ kind: 'hard-delete', rows: [row] })}
                      >
                        Hard-delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dialog.kind === 'hard-delete' && (
        <HardDeleteDialog
          open
          rows={dialog.rows}
          onClose={() => setDialog({ kind: 'idle', rows: [] })}
          onCompleted={() => {
            setDialog({ kind: 'idle', rows: [] });
            void loadRows();
          }}
        />
      )}

      {dialog.kind === 'bulk-correct' && (
        <BulkCorrectionsDialog
          open
          rows={dialog.rows}
          onClose={() => setDialog({ kind: 'idle', rows: [] })}
          onCompleted={() => {
            setDialog({ kind: 'idle', rows: [] });
            void loadRows();
          }}
        />
      )}
    </div>
  );
}

interface FiltersPanelProps {
  entityFilter: PurgeEntityType | 'all';
  setEntityFilter: (v: PurgeEntityType | 'all') => void;
  originFilter: Origin | 'all';
  setOriginFilter: (v: Origin | 'all') => void;
  resolutionFilter: CartResolutionCode | 'all';
  setResolutionFilter: (v: CartResolutionCode | 'all') => void;
  ageFilter: number;
  setAgeFilter: (v: number) => void;
  hasArtifactFilter: 'any' | 'true' | 'false';
  setHasArtifactFilter: (v: 'any' | 'true' | 'false') => void;
  testModeFilter: 'any' | 'true' | 'false';
  setTestModeFilter: (v: 'any' | 'true' | 'false') => void;
  branchFilter: string;
  setBranchFilter: (v: string) => void;
  branchOptions: ReadonlyArray<{ value: string; label: string }>;
  officerFilter: string;
  setOfficerFilter: (v: string) => void;
  nameQuery: string;
  setNameQuery: (v: string) => void;
  onApply: () => void;
}

function FiltersPanel(props: FiltersPanelProps): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-5)',
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <Filter label="Type">
        <select value={props.entityFilter} onChange={(e) => props.setEntityFilter(e.target.value as PurgeEntityType | 'all')} style={selectStyle}>
          {ENTITY_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Filter>
      <Filter label="Origin">
        <select value={props.originFilter} onChange={(e) => props.setOriginFilter(e.target.value as Origin | 'all')} style={selectStyle}>
          {ORIGINS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Filter>
      <Filter label="Resolution">
        <select value={props.resolutionFilter} onChange={(e) => props.setResolutionFilter(e.target.value as CartResolutionCode | 'all')} style={selectStyle}>
          {RESOLUTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Filter>
      <Filter label="Age">
        <select value={props.ageFilter} onChange={(e) => props.setAgeFilter(Number(e.target.value))} style={selectStyle}>
          {AGE_BUCKETS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Filter>
      <Filter label="Has artifact">
        <select value={props.hasArtifactFilter} onChange={(e) => props.setHasArtifactFilter(e.target.value as 'any' | 'true' | 'false')} style={selectStyle}>
          <option value="any">Any</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </Filter>
      <Filter label="Test mode">
        <select value={props.testModeFilter} onChange={(e) => props.setTestModeFilter(e.target.value as 'any' | 'true' | 'false')} style={selectStyle}>
          <option value="any">Any</option>
          <option value="true">Test only</option>
          <option value="false">Production only</option>
        </select>
      </Filter>
      <Filter label="Branch">
        <select value={props.branchFilter} onChange={(e) => props.setBranchFilter(e.target.value)} style={selectStyle}>
          {props.branchOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Filter>
      <Filter label="Officer code">
        <input value={props.officerFilter === 'all' ? '' : props.officerFilter} onChange={(e) => props.setOfficerFilter(e.target.value || 'all')} placeholder="Any officer" style={inputStyle} />
      </Filter>
      <Filter label="Customer name">
        <input value={props.nameQuery} onChange={(e) => props.setNameQuery(e.target.value)} placeholder="Substring" style={inputStyle} />
      </Filter>
      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={props.onApply}>Apply</Button>
      </div>
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ink-600)' }}>
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      {children}
    </label>
  );
}

function th(extra?: React.CSSProperties): React.CSSProperties {
  return {
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: 'var(--ink-600)',
    borderBottom: '1px solid var(--surface-border)',
    whiteSpace: 'nowrap',
    ...extra,
  };
}

function td(extra?: React.CSSProperties): React.CSSProperties {
  return {
    padding: '10px 12px',
    verticalAlign: 'top',
    color: 'var(--ink-900)',
    ...extra,
  };
}

const selectStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 12,
  border: '1px solid var(--ink-300)',
  borderRadius: 4,
  background: 'var(--surface-card)',
};

const inputStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 12,
  border: '1px solid var(--ink-300)',
  borderRadius: 4,
  background: 'var(--surface-card)',
};
